<script setup>
import { storeToRefs } from 'pinia';
import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useNaavixStore } from '~/naavix/store/naavix.store';

const { $t } = useCommonImports();

const auth_store = useAuthStore();
const naavix_store = useNaavixStore();
const { is_explicit_function_call_active, get_naavix_agent } = storeToRefs(naavix_store);

const state = reactive({
  is_bubble_visible: true,
  is_bubble_open: false,
  is_bubble_hovered: false,
  variant: 'mini',
});

const is_naavix_enabled = computed(() => {
  if (get_naavix_agent.value) {
    if (get_naavix_agent.value.is_enabled || auth_store.is_hacker_user || auth_store.is_manager_team_user) {
      return true;
    }
  }
  return false;
});

const texts = computed(() => {
  if (get_naavix_agent.value.module === 'terra') {
    return {
      title: $t('Construction Progress Intelligence'),
      description: $t('Monitor solar construction progress, analyze performance metrics, and stay ahead of project timelines.'),
      starter_suggestions: [
        $t(`What activities are delayed and need immediate attention this week?`),
        $t(`Compare daily module installation performance against planned work rate`),
        $t(`Which tracker installation locations show critical progress delays?`),
        $t(`Show progress status of electrical work across inverter blocks vs timeline`),
        $t(`What's the current trenching progress and how much work remains by location?`),
      ],
    };
  }
  else if (get_naavix_agent.value.module === 'forms') {
    return {
      title: get_naavix_agent.value.form_name,
      description: $t('Query form records, analyze data, detect trends, and generate insightful reports that address bottlenecks and identify core issues.'),
      starter_suggestions: [
        $t(`Which forms are currently overdue, and who is responsible for them?`),
        $t(`Summarize the forms by status for this month.`),
        $t(`Who has the largest number of open high- or critical-priority forms?`),
        $t(`What is the average resolution time for forms, broken down by priority?`),
        $t(`List all forms assigned to me and created by me, along with a breakdown of their statuses.`),
      ],
    };
  }
  else if (get_naavix_agent.value.module === 'pm') {
    return {
      title: $t('Project management'),
      description: $t('Analyze the schedule to surface critical-path risks and deliver real-time insights that keep your project on time and on budget.'),
      starter_suggestions: [
        $t(`Which critical-path activities are currently delayed, and by how many days?`),
        $t(`Which contract milestones due in the next 30 days are at risk of slipping?`),
        $t(`What is our overall schedule performance - SPI, percent complete vs. baseline, and forecast finish variance?`),
        $t(`Which resources or crews are over-allocated in the next two weeks?`),
        $t(`List the top five near-critical tasks (float < 5 days) that need immediate attention.`),
      ],
    };
  }
  return null;
});

function closeBubble() {
  state.is_bubble_open = false;
  state.variant = 'mini';
}

watch(() => state.variant, () => {
  if (state.variant === 'full') {
    document.body.style.overflow = 'hidden';
  }
  else {
    document.body.style.overflow = '';
  }
});

watch(() => get_naavix_agent.value, () => {
  state.is_bubble_open = false;
  state.variant = 'mini';
  naavix_store.$reset();
}, { deep: true });
</script>

<template>
  <div
    v-if="state.is_bubble_visible && is_naavix_enabled"
    class="fixed right-4 bottom-4 h-12 w-12 flex items-center justify-center rounded-full bubble shadow-lg cursor-pointer z-[1001]"
    @click="state.is_bubble_open = !state.is_bubble_open"
    @mouseenter="state.is_bubble_hovered = true"
    @mouseleave="state.is_bubble_hovered = false"
  >
    <IconHawkXClose v-if="state.is_bubble_open" class="text-white" />
    <IconHawkStarSix v-else class="text-white" :class="{ 'opacity-30': !get_naavix_agent.is_enabled }" />
    <Transition name="fade">
      <div
        v-if="state.is_bubble_hovered && !state.is_bubble_open"
        class="absolute -top-2 -right-2 bg-gray-300 hover:bg-gray-400 rounded-full p-1 transform transition-all duration-200 hover:scale-110"
        @click.stop="state.is_bubble_visible = false"
      >
        <IconHawkXClose class="w-3 h-3" />
      </div>
    </Transition>
  </div>
  <div v-if="state.is_bubble_visible && is_naavix_enabled && state.is_bubble_open && state.variant === 'full'" class="fixed inset-0 bg-gray-500/50 backdrop-blur-[2px] z-40" />
  <NaavixMagicEffect v-if="state.is_bubble_visible && is_naavix_enabled && state.is_bubble_open && is_explicit_function_call_active" class="z-40" />
  <Transition name="slide-up">
    <NaavixChatDialog
      v-if="state.is_bubble_visible && is_naavix_enabled && state.is_bubble_open"
      :title="texts.title"
      :description="texts.description"
      :starter-suggestions="texts.starter_suggestions"
      @close="closeBubble"
      @variant-change="state.variant = $event"
    />
  </Transition>
</template>

<style scoped>
@import "./styles/stylesheet.scss";

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: bottom right;
  will-change: transform, opacity;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
